import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useInvoices } from '../hooks/useInvoices';
import { InvoiceItem } from '../services';
import { InvoiceSortBy, InvoiceSortDir } from '../services/invoiceService';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const getStatusColor = (status: InvoiceItem['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'DRAFT': return 'warning';
    case 'POSTED': return 'primary';
    case 'PAID': return 'success';
    case 'CLOSED': return 'success';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

// Chip labels: map the enum to the existing translation keys rather than
// rendering the raw English enum member.
const STATUS_LABEL_KEYS: Record<InvoiceItem['status'], string> = {
  DRAFT: 'invoices.draft',
  POSTED: 'invoices.posted',
  PAID: 'invoices.paid',
  CLOSED: 'invoices.closed',
  CANCELLED: 'invoices.cancelled',
};

const formatDate = (dateString: string | null, locale: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString(locale === 'id' ? 'id-ID' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const InvoiceList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    invoices,
    isLoading,
    error,
    total,
    hasMore,
    isProcessingPayment,
    loadInvoices,
    loadMoreInvoices,
    downloadInvoice,
    processPayment,
    markPaid,
    downloadReceipt,
    clearError,
  } = useInvoices();

  const [statusFilter, setStatusFilter] = useState<InvoiceItem['status'] | 'all'>('all');
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all');
  const [sortBy, setSortBy] = useState<InvoiceSortBy>('invoice_date');
  const [sortDir, setSortDir] = useState<InvoiceSortDir>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    invoice: InvoiceItem | null;
    paymentUrl: string | null;
  }>({
    open: false,
    invoice: null,
    paymentUrl: null,
  });

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Customers type an invoice number a digit at a time; only ask the server
  // once they pause.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    loadInvoices(0, PAGE_SIZE, statusFilter === 'all' ? undefined : statusFilter, {
      search,
      year: yearFilter,
      sortBy,
      sortDir,
    });
  }, [loadInvoices, statusFilter, yearFilter, sortBy, sortDir, search]);

  const handleRefresh = () => {
    loadInvoices(0, PAGE_SIZE, statusFilter === 'all' ? undefined : statusFilter, {
      search,
      year: yearFilter,
      sortBy,
      sortDir,
    });
  };

  // Invoices go back several years, so offer a window rather than the three
  // hard-coded years this filter used to list.
  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => thisYear - i);
  }, []);

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      await downloadInvoice(invoiceId);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const handleProcessPayment = async (invoice: InvoiceItem) => {
    try {
      const result = await processPayment(invoice.id);
      if (result && result.success) {
        // Check for redirect_url in multiple possible locations
        const paymentUrl = result.redirect_url || 
                          result.payment_transaction?.redirect_url || 
                          result.payment_transaction?.snap_payment_url ||
                          result.payment_url; // fallback to legacy field
        
        if (paymentUrl) {
          setPaymentDialog({
            open: true,
            invoice,
            paymentUrl: paymentUrl,
          });
        } else {
          console.error('No payment URL found in response:', result);
        }
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
    }
  };

  const handleMarkPaid = async (invoiceId: string) => {
    try {
      await markPaid(invoiceId);
      setPaymentDialog(prev => ({ ...prev, open: false }));
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
    }
  };

  const handleDownloadReceipt = async (invoiceId: string) => {
    try {
      await downloadReceipt(invoiceId);
    } catch (error) {
      console.error('Failed to download receipt:', error);
    }
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog({
      open: false,
      invoice: null,
      paymentUrl: null,
    });
  };

  const openPaymentGateway = () => {
    if (paymentDialog.paymentUrl) {
      // Open Midtrans payment gateway in new tab
      const paymentWindow = window.open(paymentDialog.paymentUrl, '_blank', 'noopener,noreferrer');
      
      // Show success message if window opened successfully
      if (paymentWindow) {
        setShowSuccessSnackbar(true);
      }
      
      // Close the payment dialog after opening the payment gateway
      handleClosePaymentDialog();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('invoices.myInvoices')}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                label={t('invoices.searchLabel')}
                placeholder={t('invoices.searchPlaceholder')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={t('invoices.clearSearch')}
                        onClick={() => setSearchInput('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('invoices.filterByStatus')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('invoices.filterByStatus')}
                  onChange={(e) => setStatusFilter(e.target.value as InvoiceItem['status'] | 'all')}
                >
                  <MenuItem value="all">{t('invoices.allInvoices')}</MenuItem>
                  <MenuItem value="DRAFT">{t('invoices.draft')}</MenuItem>
                  <MenuItem value="POSTED">{t('invoices.posted')}</MenuItem>
                  <MenuItem value="PAID">{t('invoices.paid')}</MenuItem>
                  <MenuItem value="CLOSED">{t('invoices.closed')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('invoices.filterByYear')}</InputLabel>
                <Select
                  value={yearFilter}
                  label={t('invoices.filterByYear')}
                  onChange={(e) => setYearFilter(e.target.value as number | 'all')}
                >
                  <MenuItem value="all">{t('invoices.allYears')}</MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 9, md: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('invoices.sortBy')}</InputLabel>
                <Select
                  value={sortBy}
                  label={t('invoices.sortBy')}
                  onChange={(e) => setSortBy(e.target.value as InvoiceSortBy)}
                >
                  <MenuItem value="invoice_date">{t('invoices.invoiceDate')}</MenuItem>
                  <MenuItem value="due_date">{t('invoices.dueDate')}</MenuItem>
                  <MenuItem value="invoice_number">{t('invoices.invoiceNumber')}</MenuItem>
                  <MenuItem value="amount">{t('invoices.amount')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 1 }}>
              <Tooltip title={sortDir === 'desc' ? t('invoices.sortDesc') : t('invoices.sortAsc')}>
                <IconButton
                  onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
                  aria-label={sortDir === 'desc' ? t('invoices.sortDesc') : t('invoices.sortAsc')}
                  size="small"
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  {sortDir === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">
                {t('invoices.totalInvoices', { total })}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {isLoading && invoices.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {search ? t('invoices.noSearchResults', { term: search }) : t('invoices.noInvoices')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <List>
              {invoices.map((invoice, index) => (
                <React.Fragment key={invoice.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            <Link 
                              to={`/invoices/${invoice.id}`}
                              style={{ 
                                textDecoration: 'none', 
                                color: 'inherit'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                            >
                              {t('invoices.invoiceHash', { number: invoice.invoice_number })}
                            </Link>
                          </Typography>
                          <Chip
                            label={t(STATUS_LABEL_KEYS[invoice.status])}
                            color={getStatusColor(invoice.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.currency} {invoice.total_amount.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('invoices.invoiceDateLabel')} {formatDate(invoice.invoice_date, i18n.language)}
                          </Typography>
                          {invoice.due_date && (
                            <Typography variant="body2" color="text.secondary">
                              {t('invoices.dueDateLabel')} {formatDate(invoice.due_date, i18n.language)}
                            </Typography>
                          )}
                          {invoice.order_number && (
                            <Typography variant="body2" color="text.secondary">
                              {t('invoices.orderNumberLabel')} {invoice.order_number}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ ml: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {invoice.is_pdf_generated && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadInvoice(invoice.id)}
                        >
                          {t('common.download')}
                        </Button>
                      )}
                      
                      {invoice.status === 'POSTED' && (
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<PaymentIcon />}
                          onClick={() => handleProcessPayment(invoice)}
                          disabled={isProcessingPayment}
                        >
                          {isProcessingPayment ? t('invoices.processing') : t('invoices.payNow')}
                        </Button>
                      )}
                      
                      {(invoice.status === 'PAID' || invoice.status === 'CLOSED') && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          startIcon={<ReceiptIcon />}
                          onClick={() => handleDownloadReceipt(invoice.id)}
                        >
                          {t('invoices.receipt')}
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                  {index < invoices.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                </React.Fragment>
              ))}
            </List>
          </Card>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={loadMoreInvoices}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : t('invoices.loadMore')}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog.open}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('invoices.paymentForInvoice', { number: paymentDialog.invoice?.invoice_number })}
        </DialogTitle>
        <DialogContent>
          {paymentDialog.invoice && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('invoices.amount')}: {paymentDialog.invoice.currency} {paymentDialog.invoice.total_amount.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('invoices.gatewayNotice')}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  • {t('invoices.gatewayBullet1')}<br/>
                  • {t('invoices.gatewayBullet2')}<br/>
                  • {t('invoices.gatewayBullet3')}
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={openPaymentGateway}
            startIcon={<LaunchIcon />}
            endIcon={<PaymentIcon />}
          >
            {t('invoices.openGateway')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {t('invoices.gatewayOpened')}
        </Alert>
      </Snackbar>
    </Box>
  );
};
